import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Users, Star } from 'lucide-react';
import { ProofSection } from '../components/ProofSection';
import { Navigation } from '../components/Navigation';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function AboutPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-96">
      <Navigation data-id="element-97" />

      {/* Hero / Master Joe Section */}
      <section className="pt-16 pb-12 px-4 bg-gradient-to-b from-blue-50 to-white" data-id="element-98">
        <div className="max-w-4xl mx-auto text-center" data-id="element-99">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6" data-id="element-100">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1e3a8a] mb-2" data-id="element-101">
              About Master Joe Whitworth
            </h1>
            <p className="text-xl text-gray-600 font-medium" data-id="element-102">
              Founder & Head Instructor
            </p>

            <div className="relative w-48 h-48 sm:w-64 sm:h-64 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl mt-8" data-id="element-103">
              <img src="https://cdn.magicpatterns.com/uploads/e4dkdys4W4JPrBEXatM8ui/about.png" alt="Master Joe Whitworth" className="w-full h-full object-cover object-top" data-id="element-104" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Behind the Vision */}
      <section className="py-16 px-4 bg-white" data-id="element-105">
        <div className="max-w-3xl mx-auto" data-id="element-106">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-107">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a8a] mb-8 text-center" data-id="element-108">
              Behind the Vision
            </h2>

            <div className="prose prose-lg text-gray-600 mx-auto space-y-6 leading-relaxed" data-id="element-109">
              <p data-id="element-110">
                Master Joe Whitworth was born in South Korea. He lived in an
                orphanage until the age of 10, when he was adopted by his
                American, English-speaking family. When he started elementary
                school, as a foreigner he was taunted and bullied by other kids,
                so shortly after he arrived in the United States he started
                learning Taekwondo at a local school. Martial arts gave him the
                confidence he needed to assert himself.
              </p>
              <p data-id="element-111">
                Master Joe Whitworth has been an athlete for his entire life. He
                competed in a variety of sports, but it was through Taekwondo
                that he found his stride and purpose. He has instructed
                Taekwondo for over 20 years. A few years ago through a series of
                twists and turns he made the commitment to himself, his family
                and his future students that he would open his own school,
                focusing on the things that students, parents and athletes were
                searching for in a Taekwondo education.
              </p>
              <p data-id="element-112">
                At present Master Joe is a 5th degree black belt and has 25
                years of experience in teaching martial arts. Master Joe expects
                a lot from his students. He encourages his students to work hard
                at martial arts and in school. Students look to Master Joe for
                instruction on technique, for a lesson in life, for a good laugh
                after class, and for inspiration when times are tough.
              </p>
              <p className="font-semibold text-[#1e3a8a]" data-id="element-113">
                Learning the skill of martial arts has helped Master Joe grow
                into a successful and confident man. He wants to pass on and
                instill these skills in his students. Master Joe and his
                instructors give their students a little piece of themselves
                during each and every class.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Instructors Section */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-114">
        <div className="max-w-5xl mx-auto" data-id="element-115">
          <div className="text-center mb-16" data-id="element-116">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1e3a8a] mb-4" data-id="element-117">
              Our Instructors
            </h2>
            <div className="w-24 h-1 bg-[#dc2626] mx-auto rounded-full" data-id="element-118"></div>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-16" data-id="element-119">
            {/* Instructor Isaak */}
            <motion.div variants={fadeIn} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-120">
              <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-121">
                <div className="h-64 md:h-auto bg-gray-200 relative" data-id="element-122">
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100" data-id="element-123">
                    <Users className="w-16 h-16 opacity-20" data-id="element-124" />
                    <span className="sr-only" data-id="element-125">Instructor Isaak Photo</span>
                  </div>
                  {/* Placeholder for Isaak's photo */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-126"></div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-127">
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4" data-id="element-128">
                    Instructor Isaak Whitworth
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6" data-id="element-129">
                    Taekwondo has been a part of Instructor Isaak's life from a
                    very young age. The dojang is a second home for him. While
                    Instructor Isaak teaches many of the classes he is also one
                    of the highest ranking students. Instructor Isaak has also
                    competed and been a National Team Member with USAT. He is an
                    all around athlete and excellent instructor.
                  </p>
                  <div className="flex items-center gap-2 text-[#dc2626] font-semibold text-sm" data-id="element-130">
                    <Award className="w-4 h-4" data-id="element-131" />
                    <span data-id="element-132">National Team Member</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Instructor Sung Ho */}
            <motion.div variants={fadeIn} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-133">
              <div className="grid grid-cols-1 md:grid-cols-2 md:grid-flow-dense" data-id="element-134">
                <div className="p-8 md:p-12 flex flex-col justify-center order-2 md:order-1" data-id="element-135">
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4" data-id="element-136">
                    Instructor Sung Ho Park
                  </h3>
                  <p className="text-gray-600 leading-relaxed italic mb-4" data-id="element-137">
                    "A wise person once said 'Do not think about the result, but
                    always think in between.' ... Taekwondo has taught me
                    respect and given me confidence."
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6" data-id="element-138">
                    I have come a long way in the last ten years and I am so
                    proud of how far I have come through all of the training,
                    the blood, sweat and tears at times, my coaches and
                    teammates have shaped me and who I am as an instructor and
                    athlete. I always want to give back to my students as I am a
                    role model for many of them.
                  </p>
                  <div className="flex items-center gap-2 text-[#dc2626] font-semibold text-sm" data-id="element-139">
                    <Star className="w-4 h-4" data-id="element-140" />
                    <span data-id="element-141">Role Model & Mentor</span>
                  </div>
                </div>
                <div className="h-64 md:h-auto bg-gray-200 relative order-1 md:order-2" data-id="element-142">
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100" data-id="element-143">
                    <Users className="w-16 h-16 opacity-20" data-id="element-144" />
                    <span className="sr-only" data-id="element-145">Instructor Sung Ho Photo</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-146"></div>
                </div>
              </div>
            </motion.div>

            {/* Instructor Ellie */}
            <motion.div variants={fadeIn} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-147">
              <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-148">
                <div className="h-64 md:h-auto bg-gray-200 relative" data-id="element-149">
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100" data-id="element-150">
                    <Users className="w-16 h-16 opacity-20" data-id="element-151" />
                    <span className="sr-only" data-id="element-152">Instructor Ellie Photo</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-153"></div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-154">
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4" data-id="element-155">
                    Instructor Ellie Whitworth
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6" data-id="element-156">
                    The last 12 years of my life I have spent in the dojang with
                    my family. Taekwondo has changed my life in that it has
                    given me confidence and it has provided me an opportunity to
                    make goals and work diligently toward them. Even though it
                    is hard, I love it because it challenges me, and I am a
                    better person. I have more perseverance and I am a better
                    athlete because of it.
                  </p>
                  <div className="flex items-center gap-2 text-[#dc2626] font-semibold text-sm" data-id="element-157">
                    <Award className="w-4 h-4" data-id="element-158" />
                    <span data-id="element-159">12 Years Experience</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 px-4 bg-[#1e3a8a] text-white text-center" data-id="element-160">
        <div className="max-w-3xl mx-auto" data-id="element-161">
          <h2 className="text-3xl font-bold mb-6" data-id="element-162">Ready to join our family?</h2>
          <p className="text-blue-100 mb-8 text-lg" data-id="element-163">
            Experience the difference dedicated instruction makes.
          </p>
          <Link to="/" className="inline-block px-8 py-4 bg-[#dc2626] text-white text-lg font-bold rounded-lg shadow-lg hover:bg-red-700 transition-colors" data-id="element-164">
            Start Your Trial Today
          </Link>
        </div>
      </section>

      {/* Proof Section */}
      <ProofSection testimonialIndex={1} data-id="element-165" />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-166">
        <div className="max-w-4xl mx-auto" data-id="element-167">
          <div className="font-black text-xl text-white mb-4 tracking-tighter" data-id="element-168">
            NWBBA
          </div>
          <p className="mb-6" data-id="element-169">Northwest Black Belt Academy</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8" data-id="element-170">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-171">
              Home
            </Link>
            <Link to="/about" className="text-white font-bold" data-id="element-172">
              About
            </Link>
            <Link to="/classes" className="hover:text-white transition-colors" data-id="element-173">
              Classes
            </Link>
            <Link to="/schedule" className="hover:text-white transition-colors" data-id="element-174">
              Schedule
            </Link>
            <Link to="/resources" className="hover:text-white transition-colors" data-id="element-175">
              Resources
            </Link>
            <Link to="/blog" className="hover:text-white transition-colors" data-id="element-176">
              Blog
            </Link>
            <Link to="/proof" className="hover:text-white transition-colors" data-id="element-177">
              Proof
            </Link>
            <a href="#" className="hover:text-white transition-colors" data-id="element-178">
              Contact
            </a>
          </div>
          <p data-id="element-179">
            &copy; {new Date().getFullYear()} Northwest Black Belt Academy. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}